// Dialog.js - Eine generische Dialog-Komponente
import React from 'react';

/**
 * Generische Dialog-Komponente für verschiedene Anwendungsfälle
 * 
 * @param {Object} props - Komponenteneigenschaften
 * @param {boolean} props.isOpen - Gibt an, ob der Dialog geöffnet ist
 * @param {string} props.title - Der Titel des Dialogs
 * @param {React.ReactNode} props.children - Der Inhalt des Dialogs
 * @param {React.ReactNode} props.actions - Die Aktionsbuttons des Dialogs (z.B. OK, Abbrechen)
 * @param {Function} props.onClose - Callback-Funktion, die beim Schließen des Dialogs aufgerufen wird
 * @param {string} props.size - Die Größe des Dialogs ('sm', 'md', 'lg', 'xl')
 */
const Dialog = ({ 
  isOpen, 
  title, 
  children, 
  actions, 
  onClose, 
  size = 'md' 
}) => {
  if (!isOpen) return null;

  // Größenklassen für den Dialog
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  // Schließt den Dialog, wenn der Hintergrund geklickt wird
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      onClick={handleBackdropClick}
    >
      <div className={`bg-white p-6 rounded-lg shadow-lg w-full ${sizeClasses[size]} overflow-y-auto max-h-[90vh]`}>
        {/* Dialog-Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* Dialog-Inhalt */}
        <div className="mb-6">
          {children}
        </div>

        {/* Dialog-Aktionen */}
        {actions && (
          <div className="flex justify-end space-x-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;
